import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { NbSpinnerService } from "@nebular/theme";
import { OKTA_AUTH } from "@okta/okta-angular";
import type OktaAuth from "@okta/okta-auth-js";

/**
 * Landing page when a user authenticates successfully at Okta but has
 * no group that grants access to the dashboard. The entitlement guard
 * redirects here instead of calling `signOut()` inline — that used to
 * bounce through Okta /logout → / → /admin → Okta again and hang on
 * the callback spinner if the IdP session was still live.
 *
 * Clears local tokens on init so the user can't silently loop back
 * into /admin via a stray navigation. The "Sign in with different
 * account" button does the full Okta-side signOut redirect.
 */
@Component({
  selector: "ngx-access-denied",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ad-shell">
      <div class="panel" role="alert">
        <p class="title">Access denied</p>
        <p class="detail">
          Your Okta account is not provisioned for the Sevaro dashboard.
          If you believe this is an error, contact your administrator.
        </p>
        <div class="actions">
          <button
            type="button"
            class="btn btn--primary"
            [disabled]="busy()"
            (click)="signOut()"
          >
            Sign out
          </button>
        </div>
        <p class="support">
          If the problem persists, contact
          <a href="mailto:support&#64;sevaro.com">support&#64;sevaro.com</a>.
        </p>
      </div>
    </div>
  `,
  styles: [
    `
      .ad-shell {
        display: flex;
        min-height: 100vh;
        align-items: center;
        justify-content: center;
        padding: 1.5rem;
        background: #f7f9fc;
      }
      .panel {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        max-width: 28rem;
        padding: 1.75rem 2rem;
        border-radius: 0.5rem;
        border: 1px solid #edf1f7;
        background: #fff;
        box-shadow: 0 4px 16px rgba(44, 51, 73, 0.06);
        text-align: center;
      }
      .title {
        font-weight: 600;
        font-size: 1rem;
        color: #db2c66;
        margin: 0;
      }
      .detail {
        font-size: 0.8125rem;
        color: #5e6778;
        margin: 0;
      }
      .support {
        font-size: 0.75rem;
        color: #8f9bb3;
        margin: 0.5rem 0 0;
      }
      .support a {
        color: #0d62cc;
        text-decoration: none;
      }
      .support a:hover {
        text-decoration: underline;
      }
      .actions {
        margin-top: 0.5rem;
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        justify-content: center;
      }
      .btn {
        appearance: none;
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        border: 0;
        font: inherit;
        font-weight: 500;
        cursor: pointer;
      }
      .btn--primary {
        background: #0d62cc;
        color: #fff;
      }
      .btn--primary:hover:not(:disabled) {
        background: #0b55b0;
      }
      .btn:disabled {
        opacity: 0.6;
        cursor: progress;
      }

      :host-context(body.theme-dark) .ad-shell {
        background: var(--surface-0);
      }
      :host-context(body.theme-dark) .panel {
        background: var(--surface-1);
        border-color: var(--border-subtle);
        box-shadow: var(--shadow-overlay);
      }
      :host-context(body.theme-dark) .detail {
        color: var(--text-secondary);
      }
      :host-context(body.theme-dark) .support {
        color: var(--text-tertiary);
      }
      :host-context(body.theme-dark) .support a {
        color: var(--accent-primary);
      }
    `,
  ],
})
export class AccessDeniedComponent implements OnInit {
  private readonly oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private readonly spinnerService = inject(NbSpinnerService);
  readonly busy = signal(false);

  ngOnInit(): void {
    // nb-layout normally triggers this for /admin routes. We render
    // outside the layout, so the splash #nb-global-spinner would cover
    // our UI forever without it.
    this.spinnerService.load();
    // Intentionally do NOT clear tokens here — signOut() needs the
    // id_token to build id_token_hint for Okta's /logout endpoint.
    // Without it, signOut silently no-ops and the button does nothing.
  }

  async signOut(): Promise<void> {
    this.busy.set(true);
    try {
      // Okta /logout kills the IdP session, then we land at /, which
      // loginHintGuard forwards to /admin, which triggers a fresh
      // signInWithRedirect — the user sees the Okta login form and
      // can choose any account.
      await this.oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin + "/",
      });
    } catch {
      await this.oktaAuth.tokenManager.clear();
      await this.oktaAuth.signInWithRedirect({
        originalUri: "/admin",
        prompt: "login",
      });
      this.busy.set(false);
    }
  }
}
